"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { FileSpreadsheet } from "lucide-react"
import { trackEvent } from "@/lib/analytics"

const PRESS = "[@media(hover:none)]:active:opacity-60"

/**
 * The plan card as it appears in the chat, live: the card's own code
 * (garmin-mcp's ui/plan.html, copied to public/play by
 * scripts/sync-plan-card.mjs) in a frame, drawing an example. It is sharp at
 * any screen density, and it works: weeks can be picked, and its buttons
 * answer the way they do in Claude, with this page standing in for the chat.
 */
const TABS = [
  {
    key: "own",
    label: "Bring your own plan",
    line: "A spreadsheet, a PDF or a screenshot from your coach or a book. Every session lands on your watch.",
    prompt: "Here’s the plan my coach sent. Put it on my Garmin calendar with the right paces.",
    file: "coach-plan.xlsx",
    title: "A marathon plan read from a coach’s spreadsheet, drawn by the Daash plan card.",
  },
  {
    key: "plan",
    label: "Build a plan",
    line: "Every week of the block, with the next session ready to sync.",
    prompt: "Build me a 12-week marathon plan and put it on my watch.",
    file: null,
    title: "A 12-week marathon block drawn by the Daash plan card: weekly distance by phase, and the first week’s sessions.",
  },
  {
    key: "progress",
    label: "Check progress",
    line: "What you ran against the plan, moved and missed sessions included.",
    prompt: "How’s my training going?",
    file: null,
    title: "A block four weeks in, drawn by the Daash plan card: runs done with pace and heart rate, one moved, one missed.",
  },
] as const

type Tab = (typeof TABS)[number]["key"]

export function PlanCardShowcase({
  connectHref = "#connect",
  title = "Your plan, in the chat",
  intro = "Bring the plan you already follow, or ask Claude for one. It draws the block, then puts every session on your Garmin calendar.",
  first = "own",
}: { connectHref?: string; title?: string; intro?: string; first?: Tab } = {}) {
  const [tab, setTab] = useState<Tab>(first)
  const [height, setHeight] = useState(760)
  const [sent, setSent] = useState<string | null>(null)
  // Shown once the card has drawn and said how tall it is; until then a
  // shimmer holds its place, so the frame never flashes empty.
  const [ready, setReady] = useState(false)
  const frame = useRef<HTMLIFrameElement>(null)
  const shot = TABS.find((t) => t.key === tab) ?? TABS[0]

  const post = useCallback((msg: unknown) => {
    frame.current?.contentWindow?.postMessage(msg, window.location.origin)
  }, [])

  // The card talks to its host in JSON-RPC: it reports its height, and its
  // buttons ask to add a message to the chat or to read the plan again.
  useEffect(() => {
    const reply = (id: unknown) =>
      frame.current?.contentWindow?.postMessage({ jsonrpc: "2.0", id, result: {} }, window.location.origin)
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow) return
      const d = event.data
      if (!d || d.jsonrpc !== "2.0" || !d.method) return
      if (d.method === "ui/notifications/size-changed" && d.params?.height) {
        setHeight(Math.ceil(d.params.height))
        setReady(true)
      } else if (d.method === "ui/message") {
        const block = Array.isArray(d.params?.content) ? d.params.content[0] : d.params?.content
        setSent(typeof block?.text === "string" ? block.text : "")
        reply(d.id)
        trackEvent("plan_playground_action", { kind: "message", tab })
      } else if (d.method === "tools/call") {
        // The refresh button reads the plan again; a tapped session asks for
        // its details or its steps. The page answers all three from the example.
        const name = typeof d.params?.name === "string" ? d.params.name : "get_plan"
        post({ type: "daash-play", action: "call", id: d.id, name, arguments: d.params?.arguments ?? {} })
        trackEvent("plan_playground_action", { kind: name === "get_plan" ? "refresh" : name, tab })
      } else if (d.id != null) {
        reply(d.id)
      }
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [post, tab])

  return (
    <div className="flex flex-wrap items-start gap-x-14 gap-y-7">
      <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-5 min-[960px]:sticky min-[960px]:top-24">
        <div className="flex flex-col gap-2">
          <h2 className="m-0 text-[clamp(26px,3.6vw,36px)] font-bold leading-[1.08] tracking-[-0.035em]">{title}</h2>
          <p className="m-0 text-base leading-normal text-[#5B6270]">{intro}</p>
        </div>
        <div className="flex flex-col gap-2" role="tablist" aria-label="Example">
          {TABS.map((t) => {
            const on = t.key === tab
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => {
                  if (t.key === tab) return
                  setTab(t.key)
                  setSent(null)
                  setReady(false)
                  trackEvent("plan_playground_changed", { tab: t.key })
                }}
                className={`m-press flex flex-col gap-1 rounded-[14px] border p-4 text-left transition-colors duration-200 ${on ? "border-[#161B26] bg-white" : "border-[#E6E8EC] bg-transparent hover:bg-white"} ${PRESS}`}
              >
                <span className="text-base font-semibold">{t.label}</span>
                <span className="text-[14.5px] leading-normal text-[#5B6270]">{t.line}</span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex min-w-0 max-w-[720px] flex-[999_1_560px] flex-col gap-3" role="tabpanel">
        <div key={tab} className="m-bubble flex max-w-[88%] flex-col items-end gap-1.5 self-end">
          {shot.file && (
            <span className="inline-flex items-center gap-2 rounded-[12px] border border-[#D3D7DE] bg-white px-3 py-2 text-[14px] font-medium">
              <FileSpreadsheet className="size-[18px] text-[#3F8A47]" aria-hidden />
              {shot.file}
            </span>
          )}
          <p className="m-0 rounded-[14px_14px_4px_14px] bg-[#1B2130] px-3.5 py-2.5 text-[15px] leading-[1.4] text-white">
            {shot.prompt}
          </p>
        </div>
        <div className="relative">
          {!ready && <div aria-hidden className="m-shimmer absolute inset-0 rounded-[20px]" />}
          <iframe
            key={tab}
            ref={frame}
            src={`/play/plan-card.html?mode=${tab}`}
            title={shot.title}
            loading="lazy"
            onLoad={() => window.setTimeout(() => setReady(true), 400)}
            style={{ height, opacity: ready ? 1 : 0 }}
            className="m-frame relative block w-full rounded-[20px] border-0 bg-white shadow-[0_12px_40px_-24px_rgba(22,27,38,0.35)]"
          />
        </div>
        {sent ? (
          <div key={sent} className="m-rise flex flex-col gap-2 rounded-[14px] border border-[#E6E8EC] bg-white p-3.5" role="status">
            <span className="text-[13px] text-[#5B6270]">In Claude, that button adds this to the chat:</span>
            <p className="m-0 max-w-[92%] self-end rounded-[14px_14px_4px_14px] bg-[#1B2130] px-3.5 py-2.5 text-[14.5px] leading-[1.4] text-white">
              {sent}
            </p>
            <span className="text-[13px] text-[#5B6270]">Claude answers, and any change goes straight to your Garmin calendar.</span>
          </div>
        ) : (
          <span className="text-center text-[13px] leading-normal text-[#5B6270]">
            An example. Tap a week, or try Move, Easier or Ask why.{" "}
            <a href={connectHref} className="text-[#C8261F] underline-offset-[3px] hover:underline">
              Want yours? Connect Garmin.
            </a>
          </span>
        )}
      </div>
    </div>
  )
}
